import type {
    AnalyticsButtonInfo,
    TicketData,
} from '@beat-venues/common-types';
import { useTranslation } from 'next-i18next';
import { tv, VariantProps } from 'tailwind-variants';

import {
    Heading,
    MoneyBillSolidIcon,
    ShieldCheckSolidIcon,
    Text,
} from '../../atoms';
import type { CommonProps } from '../../types';
import { VenueTicketsEventTime } from './venue-tickets-event-time';
import { Card } from './../card';

import type { JSX } from 'react';

export interface VenueTicketsProps
    extends CommonProps,
        VariantProps<typeof VenueTicketsStyles> {
    tickets?: TicketData[];
    eventTitle: string;
    titleOverwrite?: string | null;
    onBuyTicketClick?: (value: AnalyticsButtonInfo) => void;
    isExpo?: boolean;
}

export const VenueTicketsStyles = tv({
    slots: {
        root: '',
        reminderButton:
            'group absolute right-4 translate-x-14 whitespace-nowrap rounded-l-none pl-12 text-right hover:translate-x-full focus:translate-x-full ',
        reminderLabel:
            'font-medium opacity-0 delay-0 group-hover:opacity-100 group-hover:delay-200 group-focus:opacity-100 group-focus:delay-200',
        reminderIcon:
            'text-buttonPrimary pointer-events-none absolute left-2 top-2.5 h-5 w-5',
    },
});

export const VenueTickets = ({
    tickets,
    className,
    onBuyTicketClick,
    eventTitle,
    isExpo,
    titleOverwrite,
    testId = 'venue-tickets',
    ...props
}: VenueTicketsProps): JSX.Element => {
    const { t } = useTranslation(['global', 'event']);

    const { root } = VenueTicketsStyles();

    return (
        <Card className={root({ className })} {...props} data-testid={testId}>
            <Card.Header
                className="pb-2"
                title={
                    titleOverwrite ??
                    t('tickets.titles.tickets', { ns: 'event' })
                }
            >
                {!isExpo && (
                    <ul className="mb-4">
                        <li className="mr-7 inline-flex items-center">
                            <ShieldCheckSolidIcon className="mr-2" />
                            <Text>
                                {t('tickets.assuranceLabels.officialTickets', {
                                    ns: 'event',
                                })}
                            </Text>
                        </li>
                        <li className="inline-flex items-center">
                            <MoneyBillSolidIcon className="mr-2" />
                            <Text>
                                {t('tickets.assuranceLabels.securePayment', {
                                    ns: 'event',
                                })}
                            </Text>
                        </li>
                    </ul>
                )}
            </Card.Header>
            {tickets && tickets.length > 0 ? (
                <ul>
                    {tickets.map((ticket, index) => (
                        <li
                            key={ticket.id + index}
                            className="flex flex-col gap-y-4 border-t-2 border-dashed border-gray-100 pb-10 pt-6 last:pb-0"
                        >
                            <Heading
                                as="h3"
                                className="mb-0 font-light leading-6"
                                size="h4"
                            >
                                {ticket.date && (
                                    <>
                                        <span className="xs:inline-block block md:block lg:inline-block">
                                            {ticket.date}
                                        </span>
                                        <span className="xs:inline-block mx-2 hidden md:hidden lg:inline-block">
                                            •
                                        </span>
                                    </>
                                )}

                                <span className="xs:inline-block block md:block lg:inline-block">
                                    {t(`venues.${ticket.venueId}.name`, {
                                        ns: 'global',
                                    })}
                                </span>
                            </Heading>
                            <ul className="flex flex-col gap-y-8 md:gap-y-4">
                                {ticket.times.map((time) => (
                                    <VenueTicketsEventTime
                                        key={time.id}
                                        eventTitle={eventTitle}
                                        ticket={ticket}
                                        time={time}
                                        onBuyTicketClick={onBuyTicketClick}
                                    />
                                ))}
                            </ul>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="border-t-2 border-dashed border-gray-100 pb-10 pt-6">
                    <Text size="lg">
                        {t('tickets.messages.noTickets', {
                            ns: 'event',
                        })}
                    </Text>
                </div>
            )}
        </Card>
    );
};

VenueTickets.EventTimes = VenueTicketsEventTime;
